/* eslint-disable  @typescript-eslint/no-explicit-any */
import React, { useState, useMemo, JSX, useCallback, useEffect } from "react";
// Ensure React is imported to access JSX.Element type
import { Typography, useMediaQuery } from "@mui/material";
import { Formik, Form } from "formik";
import {
  CardComponent,
  TextInputComponent,
  AlertComponent,
  CountryCodeWithPhoneNumber,
} from "../../../../common/components";
import {
  initialValues,
  validationSchema,
} from "./user-management-list-add.utils";
import { useSetLoading } from "../../../../common/hooks/appearance";
import leadDetailsIcon from "../../../../assets/icons/leadDetails.svg";
import "./user-management-list-add.scss";
import { getTenetRoles, updateTenetUsers } from "../../../../api/tenets";
import { AlertMessage } from "../../../tenant/tenant-details/tenant-details-types";
import { useLogin } from "../../../../common/hooks/useUser";
import {
  updateProfileUsers,
  updateTenantUsers,
} from "../../../../api/settings";
import CompanySelect from "../components/company-select/company-select";
import RoleSelect from "../components/role-select/role-select";
import { AntSwitch } from "../components/ant-switch";
interface UserManagementListAddProps {
  companyNames: { id: number; company_name: string }[];
  roles: { value: number; label: string }[];
  setViewOpen: (value: boolean) => void;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const UserManagementListAdd: React.FC<UserManagementListAddProps> = ({
  companyNames,
  roles,
  setViewOpen,
  onSuccess,
  onCancel,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = React.useState<AlertMessage>({
    open: false,
    message: "",
    type: undefined,
  });
  const [status, setStatus] = useState("ACTIVE");
  const [tenetRoles, setTenetRoles] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState<number | null>(null);

  const tenantDetails = useLogin();
  const isSuperParter = useMemo(() => {
    return tenantDetails?.tenantDetails?.tenantType === "SuperTenant";
  }, [tenantDetails?.tenantDetails?.tenantType]);

  const currentTenantId = tenantDetails?.tenantDetails?.id;
  const currentCompanyName = tenantDetails?.tenantDetails?.companyName;

  const updateTenantUserDetails = async (data: any, resetForm: any) => {
    const tenantId = isSuperParter ? data?.company : currentTenantId;

    try {
      setLoading(true);
      const payload = {
        users: [
          {
            firstName: data?.firstName,
            lastName: data?.lastName,
            email: data?.email,
            phoneNumber:
              data?.phoneNumber?.trim() === "" ? null : data?.phoneNumber,
            status: status,
            roleId: data?.role,
            isPrimaryUser: data?.role === 1 ? true : false,
            isCreate: true,
          },
        ],
      };

      let resp;
      if (isSuperParter) {
        resp = await updateTenetUsers(parseInt(tenantId), payload);
      } else {
        resp = await updateTenantUsers(parseInt(tenantId), payload);
      }
      setAlertMessage({
        open: true,
        message: "User details updated successfully.",
        type: "success",
      });
      setTimeout(() => {
        setViewOpen(false);
        if (onSuccess) onSuccess();
      }, 1500);
    } catch (err: any) {
      console.error("Error updating tenant user details:", err);
      const errorCode = err?.response?.data?.errorCode;
      let errMessage: string | JSX.Element =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Something went wrong!";

      if (errorCode === "TENANT_DB_DETAILS_NOT_FOUND") {
        errMessage = (
          <Typography sx={{ fontSize: "13px" }}>
            <span style={{ fontWeight: "bold", fontSize: "16px" }}>
              Partner Onboard Pending.
            </span>
            <br />
            Please ensure payment is completed to add the new user.
          </Typography>
        );
      }

      setAlertMessage({
        open: true,
        message: errMessage,
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  const labelSwitch = { inputProps: { "aria-label": "Switch demo" } };

  const defaultInitialValues = {
    ...initialValues,
    company: isSuperParter ? initialValues.company : currentTenantId,
  };

  const getRole = useCallback(async (tenantId: number) => {
    try {
      setLoading(true);
      const response: any = await getTenetRoles({ tenantId });
      if (response?.data?.success) {
        const roleOptions = response?.data?.data
          ?.map((item: any) => ({
            value: item?.id,
            label: item?.role,
          }))
          .filter((item: any) => item?.label !== "Super Admin");
        setTenetRoles(roleOptions);
      }
    } catch (error: any) {
      setAlertMessage({
        open: true,
        message: error?.response?.data?.message || "Something went wrong!",
        type: "error",
      });
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (defaultInitialValues.company && isSuperParter) {
      getRole(Number(defaultInitialValues.company));
    }
  }, [defaultInitialValues.company, isSuperParter, getRole]);

  useEffect(() => {
    if (!isSuperParter) return;

    if (selectedCompany) {
      getRole(selectedCompany);
    } else {
      setTenetRoles([]);
    }
  }, [selectedCompany, isSuperParter, getRole]);

  const isMobile = useMediaQuery("(max-width: 768px)");
  return (
    <>
      <div className="user-management-create-wrapper">
        <div className="user-management-create-filter-wrapper">
          <Formik
            initialValues={defaultInitialValues}
            validationSchema={validationSchema(false)}
            onSubmit={(values, { resetForm }) =>
              updateTenantUserDetails(values, resetForm)
            }
          >
            {({
              values,
              setFieldValue,
              handleSubmit,
              errors,
              touched,
              setFieldTouched,
              submitCount,
            }) => (
              <Form>
                <div className="user-management-form-wrapper">
                  <CardComponent
                    title="Add Details"
                    icon={leadDetailsIcon}
                    isMandatory={true}
                  >
                    <div className="user-management-list-edit-data">
                      <div className="user-management-list-edit-data-right">
                        <div className="user-management-list-edit-data-right-field">
                          <div className="form-group">
                            {isSuperParter ? (
                              <CompanySelect
                                value={values.company}
                                onChange={(option) => {
                                  setFieldValue("company", option);
                                  setSelectedCompany(Number(option));
                                }}
                                companyNames={companyNames}
                                disabled={false}
                                touched={submitCount > 0}
                                error={errors.company}
                                isLabelled
                                isRequired
                              />
                            ) : (
                              <TextInputComponent
                                value={currentCompanyName}
                                type="text"
                                fieldName="company"
                                label="Company"
                                optional
                                disabled
                              />
                            )}
                          </div>
                        </div>
                        {isMobile && (
                          <div className="user-management-list-edit-data-right-field">
                            <TextInputComponent
                              type="text"
                              fieldName="firstName"
                              label="First Name"
                              placeholder="Enter first name"
                              optional={true}
                              disabled={false}
                              value={values.firstName}
                              onChange={(e: any) =>
                                setFieldValue("firstName", e?.target?.value)
                              }
                              isRequired={true}
                              isAlphaNumeric={true}
                              minlength="3"
                              maxlength="50"
                            />
                          </div>
                        )}
                        <div className="user-management-list-edit-data-right-field">
                          <TextInputComponent
                            type="text"
                            fieldName="lastName"
                            label="Last Name"
                            placeholder="Enter last name"
                            optional={true}
                            disabled={false}
                            value={values.lastName}
                            onChange={(e: any) =>
                              setFieldValue("lastName", e?.target?.value)
                            }
                            isRequired={true}
                            isAlphaNumeric={true}
                            minlength="3"
                            maxlength="50"
                          />
                        </div>
                        {isMobile && (
                          <div className="user-management-list-edit-data-right-field">
                            <TextInputComponent
                              type="email"
                              fieldName="email"
                              label="Email"
                              placeholder="Enter email"
                              optional={true}
                              disabled={false}
                              value={values.email}
                              onChange={(e: any) =>
                                setFieldValue("email", e?.target?.value)
                              }
                              isRequired={true}
                            />
                          </div>
                        )}

                        <div className="user-management-list-edit-data-right-field">
                          <div className="form-group">
                            <CountryCodeWithPhoneNumber
                              fieldName="phoneNumber"
                              label="Primary Phone Number"
                              placeholder="Enter primary number"
                              onChange={(
                                value: string,
                                country: any,
                                one: any,
                                two: any
                              ) => {
                                const countryCode = country?.dialCode;

                                if (!countryCode) {
                                  console.warn(
                                    "Missing dialCode in country object"
                                  );
                                  return;
                                }

                                setFieldValue("phoneNumber", two);
                              }}
                              onBlur={() => {
                                setFieldTouched("phoneNumber", true);
                              }}
                              value={values?.phoneNumber || ""}
                              defaultCountry="us"
                              optional={false}
                              // countryCodeEditable
                            />
                            {submitCount > 0 && typeof errors.phoneNumber === "string" && (
                              <div className="error-message">
                                {errors.phoneNumber}
                              </div>
                            )}
                          </div>
                        </div>
                        {isMobile && (
                          <div className="user-management-list-edit-data-right-field role-select">
                            <RoleSelect
                              value={values.role}
                              onChange={(option) =>
                                setFieldValue("role", option)
                              }
                              roles={
                                isSuperParter
                                  ? tenetRoles.filter(
                                      (role: any) =>
                                        role.label !== "Super Admin"
                                    )
                                  : roles.filter(
                                      (role: any) =>
                                        role.label !== "Super Admin"
                                    )
                              }
                              touched={submitCount > 0}
                              error={errors.role}
                              isLabelled
                              isRequired
                            />
                          </div>
                        )}
                        <div className="user-management-list-edit-data-right-field">
                          <div className="user-management-status-switch">
                            <Typography>Status</Typography>
                            <AntSwitch
                              {...labelSwitch}
                              checked={status === "ACTIVE"}
                              onChange={(e) =>
                                setStatus(
                                  e.target.checked ? "ACTIVE" : "INACTIVE"
                                )
                              }
                              inputProps={{ "aria-label": "ant design" }}
                            />
                            <Typography className="user-mngmt-status">
                              {status}
                            </Typography>
                          </div>
                        </div>
                      </div>

                      {!isMobile && (
                        <div className="user-management-list-edit-data-right">
                          <div className="user-management-list-edit-data-right-field">
                            <TextInputComponent
                              type="text"
                              fieldName="firstName"
                              label="First Name"
                              placeholder="Enter first name"
                              optional={true}
                              disabled={false}
                              value={values.firstName}
                              onChange={(e: any) =>
                                setFieldValue("firstName", e?.target?.value)
                              }
                              isRequired={true}
                              isAlphaNumeric={true}
                              minlength="3"
                              maxlength="50"
                            />
                          </div>
                          <div className="user-management-list-edit-data-right-field">
                            <TextInputComponent
                              type="email"
                              fieldName="email"
                              label="Email"
                              placeholder="Enter email"
                              optional={true}
                              disabled={false}
                              value={values.email}
                              onChange={(e: any) =>
                                setFieldValue("email", e?.target?.value)
                              }
                              isRequired={true}
                            />
                          </div>
                          <div className="user-management-list-edit-data-right-field role-select">
                            <RoleSelect
                              value={values.role}
                              onChange={(option) =>
                                setFieldValue("role", option)
                              }
                              roles={
                                isSuperParter
                                  ? tenetRoles.filter(
                                      (role: any) =>
                                        role.label !== "Super Admin"
                                    )
                                  : roles.filter(
                                      (role: any) =>
                                        role.label !== "Super Admin"
                                    )
                              }
                              touched={submitCount > 0}
                              error={errors.role}
                              isLabelled
                              isRequired
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="user-add-buttons-wrapper">
                      <button
                        type="button"
                        className="cancel-button"
                        onClick={onCancel}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="cancel-button next-button-wrapper"
                        onClick={() => {
                          handleSubmit();
                        }}
                      >
                        Save
                      </button>
                    </div>
                  </CardComponent>
                </div>
                {alertMessage.open && (
                  <AlertComponent
                    message={alertMessage.message}
                    type={alertMessage.type || "info"}
                    open={alertMessage.open}
                    onClose={() => setAlertMessage({ open: false })}
                  />
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default UserManagementListAdd;
